import { ref, watch, type Ref } from 'vue';

export type Theme = 'light' | 'dark';

const THEME_KEY = 'dt_theme';

const isDark: Ref<boolean> = ref(readInitialTheme() === 'dark');

function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  try {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
  } catch {
    // Modo privado/storage desabilitado
  }
  if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

function applyTheme(theme: Theme): void {
  if (typeof document !== 'undefined') {
    document.documentElement.dataset['theme'] = theme;
  }
}

// Inicializa no primeiro carregamento
applyTheme(isDark.value ? 'dark' : 'light');

watch(isDark, (val) => {
  const theme: Theme = val ? 'dark' : 'light';
  applyTheme(theme);
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    // ignore
  }
});

export function useTheme(): {
  isDark: Ref<boolean>;
  theme: Ref<Theme>;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
} {
  const theme = ref<Theme>(isDark.value ? 'dark' : 'light');

  watch(isDark, (val) => {
    theme.value = val ? 'dark' : 'light';
  });

  function toggleTheme(): void {
    isDark.value = !isDark.value;
  }

  function setTheme(newTheme: Theme): void {
    isDark.value = newTheme === 'dark';
  }

  return {
    isDark,
    theme,
    toggleTheme,
    setTheme,
  };
}
